import type { ReactElement } from 'react';
import type { ViewModel } from '@/screens/generated/view-model';
import { useModelState } from '@/store/store';
import { DataSection } from './Data';
import { DisplaySection } from './Display';
import { IntegrationsSection } from './Integrations';
import { LockoutSection } from './Lockout';
import { SecuritySection } from './Security';
import { TradingSection } from './Trading';

/** The Settings sections the design package left unbuilt.
 *
 *  Its rail names nine. It ships markup for two — Profile and Notifications — and renders the other seven as a card
 *  reading *"This section is available in the full build"*. This is that build: the placeholder is replaced by this
 *  component through the transpiler's binding overlay, so the reference file stays verbatim and the rest of the
 *  screen — rail, header, layout — is still the package's own markup.
 *
 *  These are **composed surfaces** (SDD §7.3): the reference has no DOM here to be faithful to, so there is nothing
 *  for the visual suite to diff them against. They are built to the design language in `design-reference/CLAUDE.md`
 *  and carry their own frozen baselines, exactly as Trading Intel, Billing and the investor portal do (ESC-003).
 *
 *  Danger Zone is not among them. The package lists it in the rail, and everything it would hold — the erasure
 *  request, its thirty-day hold, what is destroyed and what is anonymised — is already in Data Management, where a
 *  trader looking for it will actually be. A second screen holding the same one irreversible action would be a
 *  second place to get it wrong.
 */
export function SettingsSection({ m }: { m: ViewModel }): ReactElement | null {
  const { setSection } = useModelState();

  switch (setSection) {
    case 'security':
      return <SecuritySection />;
    case 'lockout':
      return <LockoutSection />;
    case 'display':
      return <DisplaySection />;
    case 'trading':
      return <TradingSection />;
    case 'integrations':
      return <IntegrationsSection />;
    case 'data':
    case 'danger':
      return <DataSection />;
    default:
      // Profile and Notifications have the package's own markup and never reach here. Anything else is a rail entry
      // added to the design without a section behind it, and it says so rather than rendering an empty card.
      return <Unbuilt label={String(m.setSectionLabel ?? setSection)} />;
  }
}

function Unbuilt({ label }: { label: string }): ReactElement {
  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--card-line)',
        boxShadow: 'var(--shadow-card)',
        borderRadius: '16px',
        padding: '40px 20px',
        textAlign: 'center',
        color: 'var(--muted2)',
      }}
    >
      <i className="ph ph-gear-six" style={{ fontSize: '30px', color: 'var(--ghost)' }}></i>
      <div style={{ fontSize: '13px', marginTop: '10px', color: 'var(--text-mid)' }}>{label}</div>
      <div style={{ fontSize: '11.5px', color: 'var(--faint)', marginTop: '4px' }}>
        This section has no screen behind it yet.
      </div>
    </div>
  );
}
